import { type FC, Fragment } from "react";
import { Table } from "@root/shared/components/Table/legacy/Table";
import { Tr } from "@root/shared/components/Table/legacy/Tr";
import { Th } from "@root/shared/components/Table/legacy/Th";
import { Td } from "@root/shared/components/Table/legacy/Td";
import { LazyLoadImage } from "@root/shared/components/LazyLoadImage";
import { Link } from "@root/shared/components/Link";
import { Icon } from "@root/shared/components/Icon";
import { MultiLineText } from "@root/shared/components/MultiLineText";
import { DetailTableRow } from "@clientFrontend/features/publication/components/DetailTableRow";
import type { ShopContent } from "./type";
import * as styles from "./styles.css";

type Props = {
  editUrl: string;
  canEdit: boolean;
  shopContent: ShopContent;
  dummyImgPath: string;
  site: "biyo" | "kaigo";
  isShikaPlan: boolean;
};

export const ShopContentDetail: FC<Props> = ({
  editUrl,
  canEdit,
  shopContent,
  dummyImgPath,
  site,
  isShikaPlan,
}) => {
  return (
    <>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>店舗情報</h2>
        <Table>
          <DetailTableRow header="店舗名">{shopContent.name}</DetailTableRow>
          <DetailTableRow header="店舗名フリガナ">{shopContent.nameKana}</DetailTableRow>
          <DetailTableRow header="職場の画像">
            {shopContent.clientImage && (
              <figure className={styles.imageContainer}>
                <LazyLoadImage
                  src={shopContent.clientImage.src}
                  alt="職場の画像"
                  dummyImgPath={dummyImgPath}
                  width={280}
                  height={158}
                />
              </figure>
            )}
          </DetailTableRow>
          <DetailTableRow header="電話番号">{shopContent.tel}</DetailTableRow>
          <DetailTableRow header="郵便番号">{shopContent.zipCode}</DetailTableRow>
          <DetailTableRow header="都道府県">{shopContent.prefecture}</DetailTableRow>
          <DetailTableRow header="市区町村">{shopContent.city}</DetailTableRow>
          <DetailTableRow header="町域・番地">{shopContent.address1}</DetailTableRow>
          <DetailTableRow header="建物名・部屋番号">{shopContent.address2}</DetailTableRow>
          <DetailTableRow header="マップ">
            {shopContent.latitude && shopContent.longitude && (
              <div className={`map_canvas ${styles.mapContainer}`} data-rejob-map>
                <div data-lat={shopContent.latitude} data-lng={shopContent.longitude} />
              </div>
            )}
          </DetailTableRow>
          <DetailTableRow header="最寄り駅">
            {shopContent.stations &&
              shopContent.stations.map((station) => (
                <Fragment key={station}>
                  {station}
                  <br />
                </Fragment>
              ))}
          </DetailTableRow>
          <DetailTableRow header="勤務地詳細">
            {shopContent.access && <MultiLineText text={shopContent.access} />}
          </DetailTableRow>
        </Table>
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>ブランド情報</h2>
        <Table>
          <DetailTableRow header="ブランド名">{shopContent.brand?.name}</DetailTableRow>
          <DetailTableRow header="ブランドコンセプト">
            {shopContent.brand?.concept && <MultiLineText text={shopContent.brand.concept} />}
          </DetailTableRow>
          <DetailTableRow header="ブランドロゴ画像">
            {shopContent.brand?.logoImage && (
              <figure className={styles.imageContainer}>
                <LazyLoadImage
                  src={shopContent.brand.logoImage.src}
                  alt="ブランドロゴ画像"
                  dummyImgPath={dummyImgPath}
                  width={280}
                  height={158}
                />
              </figure>
            )}
          </DetailTableRow>
          <DetailTableRow header="ブランドイメージ画像">
            {shopContent.brand?.image && (
              <figure className={styles.imageContainer}>
                <LazyLoadImage
                  src={shopContent.brand.image.src}
                  alt="ブランドイメージ画像"
                  dummyImgPath={dummyImgPath}
                  width={280}
                  height={158}
                />
              </figure>
            )}
          </DetailTableRow>
        </Table>
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>業務内容</h2>
        {shopContent.serviceContents?.map((serviceContent) => (
          <Table
            key={`業務内容_${serviceContent.menus?.join("_")}`}
            className={styles.serviceContentTable}
          >
            <DetailTableRow header="職種">{serviceContent.businessTypeName}</DetailTableRow>
            <DetailTableRow header="業務内容">
              <ul>
                {serviceContent.menus?.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </DetailTableRow>
            <DetailTableRow header="メニューの備考">
              {serviceContent.menuText && <MultiLineText text={serviceContent.menuText} />}
            </DetailTableRow>
            {!isShikaPlan && (
              <DetailTableRow header="顧客単価">
                {serviceContent.customerSpending && `${serviceContent.customerSpending}円`}
              </DetailTableRow>
            )}
          </Table>
        ))}
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>施設について</h2>
        <Table>
          <DetailTableRow header="施設形態">
            <ul>
              {shopContent.facility?.names?.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </DetailTableRow>
          {isShikaPlan && (
            <DetailTableRow header="感染症対策">
              <ul>
                {shopContent.facility?.infectionControlMeasures?.map((infectionControlMeasure) => (
                  <li key={infectionControlMeasure}>{infectionControlMeasure}</li>
                ))}
              </ul>
            </DetailTableRow>
          )}
          {isShikaPlan && (
            <DetailTableRow header="滅菌方法">
              <ul>
                {shopContent.facility?.sterilizationMethods?.map((sterilizationMethod) => (
                  <li key={sterilizationMethod}>{sterilizationMethod}</li>
                ))}
              </ul>
            </DetailTableRow>
          )}
          {isShikaPlan && (
            <DetailTableRow header="診察設備">
              <ul>
                {shopContent.facility?.equipments?.map((equipment) => (
                  <li key={equipment}>{equipment}</li>
                ))}
              </ul>
            </DetailTableRow>
          )}
          {isShikaPlan && (
            <DetailTableRow header="休診日/診療日">
              {shopContent.facility?.businessDay && (
                <MultiLineText text={shopContent.facility?.businessDay} />
              )}
            </DetailTableRow>
          )}
          <DetailTableRow header={isShikaPlan ? "診療時間" : "営業時間"}>
            {shopContent.facility?.businessTime}
          </DetailTableRow>
          {isShikaPlan && (
            <DetailTableRow header="チェア数/ユニット数">
              {shopContent.facility?.numberOfSeats && (
                <MultiLineText text={shopContent.facility?.numberOfSeats} />
              )}
            </DetailTableRow>
          )}
        </Table>
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>スタッフ情報</h2>
        <Table>
          {isShikaPlan && (
            <DetailTableRow header="院長名">{shopContent.staff?.representativeName}</DetailTableRow>
          )}
          {isShikaPlan && (
            <DetailTableRow header="院長略歴">
              {shopContent.staff?.representativeBriefHistory && (
                <MultiLineText text={shopContent.staff?.representativeBriefHistory} />
              )}
            </DetailTableRow>
          )}
          <DetailTableRow header="スタッフの人数">{shopContent.staff?.count}人</DetailTableRow>
          <DetailTableRow header="スタッフの男女別人数">
            <b>女性</b>
            <br />
            {shopContent.staff?.femaleCount}人
            <br />
            <b>男性</b>
            <br />
            {shopContent.staff?.maleCount}人
          </DetailTableRow>
          {site === "kaigo" && (
            <DetailTableRow header="シフト別のスタッフ数">
              <b>早番</b>
              <br />
              {shopContent.staff?.countByShift?.early}人
              <br />
              <b>遅番</b>
              <br />
              {shopContent.staff?.countByShift?.late}人<b>日勤</b>
              <br />
              {shopContent.staff?.countByShift?.day}人<b>夜勤</b>
              <br />
              {shopContent.staff?.countByShift?.night}人
            </DetailTableRow>
          )}

          <DetailTableRow header="スタッフの勤続年数">
            <b>1年未満</b>
            <br />
            {shopContent.staff?.countByYearsOfService?.lessThanOne}人
            <br />
            <b>1~3年未満</b>
            <br />
            {shopContent.staff?.countByYearsOfService?.oneToTwo}人
            <br />
            <b>3~5年未満</b>
            <br />
            {shopContent.staff?.countByYearsOfService?.threeToFour}人
            <br />
            <b>5~10年未満</b>
            <br />
            {shopContent.staff?.countByYearsOfService?.fiveToNine}人
            <br />
            <b>10年以上</b>
            <br />
            {shopContent.staff?.countByYearsOfService?.tenOrMore}人
          </DetailTableRow>
          <DetailTableRow header="スタッフの雇用形態">
            <b>正社員</b>
            <br />
            {shopContent.staff?.countByEmploymentType?.regularMembers}人
            <br />
            <b>アルバイト・パート</b>
            <br />
            {shopContent.staff?.countByEmploymentType?.arbeit}人<br />
            <b>業務委託</b>
            <br />
            {shopContent.staff?.countByEmploymentType?.businessConsignment}人
            <br />
            <b>契約社員</b>
            <br />
            {shopContent.staff?.countByEmploymentType?.contractEmployee}人
            <br />
            <b>その他</b>
            <br />
            {shopContent.staff?.countByEmploymentType?.other}人
          </DetailTableRow>
          <DetailTableRow header="スタッフの平均年齢">
            {shopContent.staff?.staffAverageAge}歳
          </DetailTableRow>
          {shopContent.staff?.staffProfiles?.map((profile, index) => (
            <Tr key={`${profile.name}_${profile.position}`}>
              <Th className={styles.th}>
                スタッフプロフィール
                <br />
                スタッフ：{index + 1}
              </Th>
              <Td className={styles.staffProfileData}>
                {profile.image && (
                  <figure className={styles.staffImageContainer}>
                    <LazyLoadImage
                      src={profile.image.src}
                      alt="スタッフプロフィール画像"
                      dummyImgPath={dummyImgPath}
                      width={158}
                      height={158}
                    />
                  </figure>
                )}
                <b>名前</b>
                <br />
                {profile.name}
                <br />
                <b>ひらがな</b>
                <br />
                {profile.nameKana}
                <br />
                <b>役職</b>
                <br />
                {profile.position}
                <br />
                <b>経験年数</b>
                <br />
                {profile.experienceYear && `${profile.experienceYear}年`}
                <br />
                <b>得意な施術/スタイル</b>
                <br />
                {profile.specialityTreatment}
                <br />
                <b>一言メッセージ</b>
                {profile.message && <MultiLineText text={profile.message} />}
              </Td>
            </Tr>
          ))}
        </Table>
      </section>
      {site === "kaigo" && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle[site]}>入居者・利用者について</h2>
          <Table>
            <DetailTableRow header="入居者・利用者の男女別人数">
              <b>女性</b>
              <br />
              {shopContent.resident?.femaleCount}人
              <br />
              <b>男性</b>
              <br />
              {shopContent.resident?.maleCount}人
            </DetailTableRow>
            <DetailTableRow header="定員に対する入居者・利用者の割合">
              <b>定員</b>
              <br />
              {shopContent.resident?.capacity}人
              <br />
              <b>入居者・利用者数</b>
              <br />
              {shopContent.resident?.count}人
            </DetailTableRow>
            <DetailTableRow header="職員一人あたりの入居者・利用者数">
              {shopContent.resident?.countPerStaff}人
            </DetailTableRow>
          </Table>
        </section>
      )}
      {site === "biyo" && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle[site]}>顧客について</h2>
          <Table>
            {isShikaPlan && (
              <DetailTableRow header="来院患者数">
                約{shopContent.customer?.numberOfCustomers}名/日
              </DetailTableRow>
            )}
            <DetailTableRow header="新規顧客比率">
              {shopContent.customer?.newCustomerRate?.toString()?.concat("%")}
            </DetailTableRow>
            <DetailTableRow header={isShikaPlan ? "患者の年齢層" : "客層"}>
              <ul>
                {shopContent.customer?.types?.map((type) => (
                  <li key={type}>{type}</li>
                ))}
              </ul>
            </DetailTableRow>
          </Table>
        </section>
      )}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>職場の詳細</h2>
        <Table>
          <DetailTableRow header="職場からのメッセージ">
            {shopContent.workplaceComment && <MultiLineText text={shopContent.workplaceComment} />}
          </DetailTableRow>
          <DetailTableRow header="職場の雰囲気">
            {shopContent.workplaceAtmosphere && (
              <MultiLineText text={shopContent.workplaceAtmosphere} />
            )}
          </DetailTableRow>
          <DetailTableRow header="どんな人が活躍しているか">
            {shopContent.successPerson && <MultiLineText text={shopContent.successPerson} />}
          </DetailTableRow>
          <DetailTableRow header="スキルアップ・教育制度">
            {shopContent.educationSystem && <MultiLineText text={shopContent.educationSystem} />}
          </DetailTableRow>
        </Table>
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>店舗について</h2>
        <Table>
          <DetailTableRow header="店舗アルバム">
            {shopContent.albums?.map((album, index) => (
              <div key={album.image?.id} className={styles.albumContainer}>
                <b>画像：{index + 1}</b>
                <div className={styles.albumContent}>
                  <figure className={styles.albumImageContainer}>
                    <LazyLoadImage
                      src={album.image?.src}
                      alt="店舗アルバム画像"
                      dummyImgPath={dummyImgPath}
                      width={280}
                      height={158}
                    />
                  </figure>
                  <div className={styles.albumMessage}>
                    {album.message && <MultiLineText text={album.message} />}
                  </div>
                </div>
              </div>
            ))}
          </DetailTableRow>
        </Table>
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>その他の設定</h2>
        <Table>
          <DetailTableRow header="見学の可否">
            {shopContent.salonTour ? "見学可能" : "見学不可"}
          </DetailTableRow>
          <DetailTableRow header="通知">
            <b>SMS(ショートメッセージ)を受け取る電話番号</b>
            <br />
            {shopContent.telForSms}
            <br />
            <b>連絡先メールアドレス</b>
            <br />
            <ul className={styles.notDotList}>
              {shopContent.emails?.map((email) => (
                <li key={email}>{email}</li>
              ))}
            </ul>
          </DetailTableRow>
        </Table>
      </section>
      <div className={styles.editButtonContainer}>
        <Link href={editUrl} theme={canEdit ? "primary" : "disabled"} className={styles.editButton}>
          <Icon name="edit" />
          編集する
        </Link>
      </div>
    </>
  );
};
